import { useState, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { BRAND, TIERS, YARD_SIGN_OPTIONS, BLOOMERANG_CONFIG } from "../constants";
import { validateContact } from "../utils/validation.js";
import { getPaymentAdapter } from "../utils/paymentAdapters/index.js";
import StepIndicator from "./StepIndicator.jsx";
import TierSelection from "./TierSelection.jsx";
import ContactForm from "./ContactForm.jsx";
import PaymentForm from "./PaymentForm.jsx";
import BloomerangForm, { preload as preloadBloomerangForm } from "./BloomerangForm.jsx";
import TributeFormStep from "./TributeFormStep.jsx";
import SuccessStep from "./SuccessStep.jsx";

var USE_DIRECT_FORM = import.meta.env.VITE_USE_DIRECT_FORM === "true";

// Preload Bloomerang scripts on page load so the form is instant when the modal opens.
// Skip on localhost — the mock donation UI is used instead.
if (USE_DIRECT_FORM && window.location.hostname !== "localhost") {
  preloadBloomerangForm();
}

var SESSION_KEY = "stepup_session";

function saveSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export default function StepUpFlow({ onClose, onTributesSubmitted, skipToTributes }) {
  // Lock body scroll while modal is open (critical for iframe embed)
  useEffect(function () {
    var prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function () { document.body.style.overflow = prev; };
  }, []);

  var adapterRef = useRef(null);
  var [adapterReady, setAdapterReady] = useState(false);
  var [coverFees, setCoverFees] = useState(false);

  var [step, setStep] = useState(skipToTributes ? "tributes" : (USE_DIRECT_FORM ? "donation" : "tier"));
  var [tier, setTier] = useState(TIERS[0]);
  var [yardSign, setYardSign] = useState(YARD_SIGN_OPTIONS[0]);
  var [contact, setContact] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    address: "", city: "", state: "GA", zip: "",
  });
  var [contactErrors, setContactErrors] = useState({});
  var [processing, setProcessing] = useState(false);
  var [fromName, setFromName] = useState("");
  var [cardError, setCardError] = useState("");
  var [submitError, setSubmitError] = useState("");

  // Multi-tribute state
  var [donationId, setDonationId] = useState("");
  var [tributes, setTributes] = useState({});
  var [currentDraftId, setCurrentDraftId] = useState(null);
  var [entry, setEntry] = useState({ teacher: "", school: "", message: "" });
  var [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Derived values
  var tributeValues = Object.keys(tributes).map(function (id) {
    return Object.assign({ id: id }, tributes[id]);
  });
  var submittedTributes = tributeValues.filter(function (t) { return t.status === "submitted"; })
    .sort(function (a, b) { return a.tributeNumber - b.tributeNumber; });
  var failedTributes = tributeValues.filter(function (t) { return t.status === "failed"; })
    .sort(function (a, b) { return a.tributeNumber - b.tributeNumber; });
  var submittedCount = submittedTributes.length;
  var maxTributes = tier.tributes;
  var atMax = submittedCount >= maxTributes;
  var tributeNumber = submittedCount + failedTributes.length + 1;

  // Log donation flow opened
  useEffect(function () {
    Sentry.captureMessage(
      "donate: flow opened" + (USE_DIRECT_FORM ? " (direct form)" : " (legacy adapter)"),
      "info"
    );
  }, []);

  // Initialize the payment adapter (legacy scaffolding path only)
  useEffect(function () {
    if (USE_DIRECT_FORM) return;
    Sentry.logger.info("Donation flow opened", { paymentMode: BLOOMERANG_CONFIG.paymentAdapter });
    getPaymentAdapter(BLOOMERANG_CONFIG.paymentAdapter).then(function (adapter) {
      adapter.init(BLOOMERANG_CONFIG);
      adapterRef.current = adapter;
      setAdapterReady(true);
      Sentry.logger.info("Payment adapter ready", { paymentMode: BLOOMERANG_CONFIG.paymentAdapter });
    }).catch(function (err) {
      Sentry.logger.error("Payment adapter failed to init", { error: err.message });
    });
  }, []);

  var handleContactChange = function (key, val) {
    var updated = { ...contact };
    updated[key] = val;
    setContact(updated);
    if (contactErrors[key]) {
      var errs = { ...contactErrors };
      delete errs[key];
      setContactErrors(errs);
    }
  };

  var handleEntryChange = function (field, val) {
    var updated = { ...entry };
    updated[field] = val;
    setEntry(updated);
  };

  var canProceed = (function () {
    if (step === "tier") return true;
    if (step === "contact") return true;
    if (step === "payment") return true;
    if (step === "tributes") return fromName.trim() && entry.teacher.trim() && entry.message.trim();
    return false;
  })();

  var processPayment = function () {
    setProcessing(true);
    setCardError("");

    var adapter = adapterRef.current;
    if (!adapter) {
      setCardError("Payment system is loading. Please try again.");
      setProcessing(false);
      return;
    }

    var donationAmount = tier.price;
    if (coverFees) {
      donationAmount = Math.round(
        ((tier.price + BLOOMERANG_CONFIG.transactionFee) / (1 - BLOOMERANG_CONFIG.transactionFeeRate)) * 100
      ) / 100;
    }

    adapter.setDonorInfo(contact);
    adapter.setDonationInfo({ amount: donationAmount, note: "", yardSign: yardSign });

    Sentry.logger.info("Payment started", { tier: tier.id, amount: donationAmount, yardSign: yardSign, coverFees: coverFees });

    adapter.submitPayment()
      .then(function (result) {
        var bloomerangResponse = (result && result.response) || {};
        Sentry.logger.info("Payment succeeded", {
          tier: tier.id,
          amount: donationAmount,
          bloomerangResponse: JSON.stringify(bloomerangResponse),
        });
        adapterRef.bloomerangResponse = bloomerangResponse;
        setProcessing(false);

        // Initialize multi-tribute session
        var newDonationId = crypto.randomUUID();
        Sentry.setTag("donationId", newDonationId);
        setDonationId(newDonationId);
        setTributes({});
        setCurrentDraftId(null);
        if (!fromName) setFromName(contact.firstName + " " + contact.lastName);

        saveSession({
          donationId: newDonationId,
          tier: tier.id,
          maxTributes: tier.tributes,
          fromName: fromName || (contact.firstName + " " + contact.lastName),
          email: contact.email,
          tributes: {},
        });

        setStep("tributes");
      })
      .catch(function (err) {
        var msg = (err && err.message) || "Payment failed. Please try again.";
        Sentry.logger.error("Payment failed", { tier: tier.id, amount: donationAmount, error: msg });
        setProcessing(false);
        setCardError(msg);
      });
  };

  var submitTribute = function (tributeId, tributeData) {
    // Use provided tribute (for retry) or current form
    var id = tributeId || currentDraftId || crypto.randomUUID();
    var teacher = tributeData ? tributeData.teacher : entry.teacher.trim();
    var school = tributeData ? tributeData.school : entry.school || "";
    var message = tributeData ? tributeData.message : entry.message.trim();

    if (!teacher || !message) return;

    setProcessing(true);
    setSubmitError("");

    // Ensure tribute exists in state
    var updatedTributes = { ...tributes };
    if (!updatedTributes[id]) {
      updatedTributes[id] = {
        tributeNumber: tributeNumber,
        teacher: teacher,
        school: school,
        message: message,
        status: "draft",
      };
    }
    updatedTributes[id].status = "submitting";
    setTributes(updatedTributes);

    var payload = {
      id: id,
      donationId: donationId,
      teacher: teacher,
      school: school,
      from: fromName.trim(),
      message: message,
      email: contact.email || "",
      tier: tier.id || "",
    };

    Sentry.setTag("tributeId", id);
    Sentry.setTag("donationId", donationId);
    Sentry.logger.info("Tribute submission started", { tributeId: id, teacher: teacher, school: school });

    fetch("/api/tribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(data.error || "Submission failed");
          });
        }
        return res.json();
      })
      .then(function () {
        Sentry.logger.info("Tribute submitted", { tributeId: id, teacher: teacher, tier: tier.id });

        var updated = { ...updatedTributes };
        updated[id] = Object.assign({}, updated[id], { status: "submitted" });
        setTributes(updated);
        setProcessing(false);

        // Clear form for next tribute
        setEntry({ teacher: "", school: "", message: "" });
        setCurrentDraftId(null);

        // Update localStorage
        var sessionTributes = {};
        Object.keys(updated).forEach(function (tid) {
          var t = updated[tid];
          sessionTributes[tid] = {
            tributeNumber: t.tributeNumber,
            teacher: t.teacher,
            school: t.school,
            message: t.message,
            status: t.status,
          };
        });
        saveSession({
          donationId: donationId,
          tier: tier.id,
          maxTributes: maxTributes,
          fromName: fromName,
          email: contact.email,
          tributes: sessionTributes,
        });

        onTributesSubmitted([]);

        // Auto-advance if at max
        var newSubmitted = Object.values(updated).filter(function (t) { return t.status === "submitted"; }).length;
        if (newSubmitted >= maxTributes) {
          setStep("success");
        }
      })
      .catch(function (err) {
        setProcessing(false);

        if (window.location.hostname === "localhost" && err.message === "Failed to fetch") {
          console.log("[StepUpFlow] No API available (dev mode) — marking as submitted");
          var updated = { ...updatedTributes };
          updated[id] = Object.assign({}, updated[id], { status: "submitted" });
          setTributes(updated);
          setEntry({ teacher: "", school: "", message: "" });
          setCurrentDraftId(null);
          onTributesSubmitted([]);
          var newSubmitted = Object.values(updated).filter(function (t) { return t.status === "submitted"; }).length;
          if (newSubmitted >= maxTributes) {
            setStep("success");
          }
          return;
        }

        var msg = err.message || "Something went wrong. Please try again.";
        Sentry.logger.error("Tribute submission failed", { tributeId: id, error: msg });
        setSubmitError(msg);

        var updated = { ...updatedTributes };
        updated[id] = Object.assign({}, updated[id], { status: "failed" });
        setTributes(updated);
        setEntry({ teacher: "", school: "", message: "" });
        setCurrentDraftId(null);
      });
  };

  var handleRetry = function (tributeId) {
    var t = tributes[tributeId];
    if (!t) return;
    submitTribute(tributeId, t);
  };

  var handleDone = function () {
    Sentry.captureMessage("donate: flow completed — " + submittedCount + " tribute(s)", "info");
    setStep("success");
  };

  var requestClose = function () {
    if (processing) return;
    if (step === "success") { onClose(); return; }
    setShowCloseConfirm(true);
  };

  var handleWriteMore = function () {
    setStep("tributes");
  };

  // Direct-form path: Bloomerang's form handles donation end-to-end,
  // then calls this to bridge into the tribute-writing flow.
  var handlePaymentSuccess = function (donation) {
    Sentry.setUser({ email: donation.donorEmail });
    Sentry.setTag("donationId", donation.donationId);
    Sentry.addBreadcrumb({
      category: "payment",
      message: "Bloomerang donation completed",
      data: { tier: donation.tier, amount: donation.amount, donationId: donation.donationId },
      level: "info",
    });
    Sentry.captureMessage(
      "donate: payment succeeded — $" + donation.amount + " (" + donation.tier + ")",
      "info"
    );

    var newDonationId = donation.donationId || crypto.randomUUID();
    setDonationId(newDonationId);
    setTributes({});
    setCurrentDraftId(null);
    setFromName(donation.fromName);
    setContact(function (prev) {
      return Object.assign({}, prev, { email: donation.donorEmail });
    });

    // Derive tier object from the donation amount
    var matchedTier = TIERS.find(function (t) { return t.id === donation.tier; }) || TIERS[0];
    setTier(matchedTier);

    saveSession({
      donationId: newDonationId,
      tier: donation.tier,
      maxTributes: donation.maxTributes,
      fromName: donation.fromName,
      email: donation.donorEmail,
      tributes: {},
    });

    setStep("tributes");
  };

  var goNext = function () {
    if (step === "tier") {
      Sentry.logger.info("Tier selected", { tier: tier.id, price: tier.price, yardSign: yardSign });
      setStep("contact");
    }
    else if (step === "contact") {
      var errors = validateContact(contact);
      if (Object.keys(errors).length > 0) { setContactErrors(errors); return; }
      setContactErrors({});
      if (!fromName) setFromName(contact.firstName + " " + contact.lastName);
      Sentry.setUser({ email: contact.email });
      Sentry.logger.info("Contact info submitted", { email: contact.email });
      setStep("payment");
    }
    else if (step === "payment") {
      processPayment();
    }
    else if (step === "tributes") {
      submitTribute();
    }
  };

  var goBack = function () {
    if (step === "contact") setStep("tier");
    else if (step === "payment") setStep("contact");
  };

  var showBack = !USE_DIRECT_FORM && (step === "contact" || step === "payment") && !skipToTributes;

  var nextLabel = (function () {
    if (step === "payment") return processing ? "" : "Donate $" + tier.price;
    if (step === "tributes") {
      if (processing) return "Submitting...";
      if (submittedCount === maxTributes - 1) return "Submit Final Tribute";
      return "Submit Tribute";
    }
    return "Continue";
  })();

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px", animation: "fadeIn 0.2s ease",
      }}
    >
      <div style={{
        background: "#fff", borderRadius: "10px", maxWidth: step === "donation" ? "720px" : "480px", width: "100%",
        maxHeight: "88vh", overflow: "auto", overscrollBehavior: "contain",
        padding: "28px 24px", position: "relative",
        boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
      }}>
        <button
          onClick={requestClose}
          style={{
            position: "absolute", top: "14px", right: "14px",
            background: "#F0F0F0", border: "none", borderRadius: "50%",
            width: "30px", height: "30px", cursor: "pointer", fontSize: "14px",
            color: "#999", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >{"\u2715"}</button>

        {!skipToTributes && !USE_DIRECT_FORM && <StepIndicator step={step} />}

        {step === "donation" && (
          window.location.hostname === "localhost" ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#222", margin: "0 0 8px" }}>Mock Donation (localhost)</h3>
              <p style={{ fontSize: "13px", color: "#888", margin: "0 0 16px" }}>Bloomerang form skipped on localhost. Pick a tier to jump to tributes.</p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                {TIERS.map(function (t) {
                  return (
                    <button
                      key={t.id}
                      onClick={function () {
                        handlePaymentSuccess({
                          donationId: "mock-" + crypto.randomUUID(),
                          donorEmail: "test@localhost",
                          fromName: "Test Donor",
                          amount: t.price,
                          tier: t.id,
                          maxTributes: t.tributes,
                        });
                      }}
                      style={{
                        background: BRAND.primary, color: "#fff", border: "none",
                        borderRadius: "6px", padding: "12px 24px", fontSize: "14px",
                        fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {"$" + t.price + " — " + t.tributes + " tributes"}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <BloomerangForm
              onPaymentSuccess={handlePaymentSuccess}
              onError={function (msg) {
                var validationMessages = [
                  "Please fill in your card details",
                  "Valid card info is required",
                  "Your card number is incomplete",
                  "Your card's expiration date is incomplete",
                  "Your card's security code is incomplete",
                ];
                var isValidation = validationMessages.some(function (v) { return msg && msg.indexOf(v) !== -1; });
                Sentry.captureMessage("donate: payment " + (isValidation ? "validation" : "failed") + " — " + msg, isValidation ? "warning" : "error");
              }}
            />
          )
        )}
        {step === "tier" && (
          <TierSelection tier={tier} yardSign={yardSign} onTierChange={setTier} onYardSignChange={setYardSign} />
        )}
        {step === "contact" && (
          <ContactForm contact={contact} onContactChange={handleContactChange} errors={contactErrors} />
        )}
        {step === "payment" && (
          <PaymentForm
            processing={processing}
            tierPrice={tier.price}
            cardError={cardError}
            paymentAdapter={BLOOMERANG_CONFIG.paymentAdapter}
            adapter={adapterRef.current}
            coverFees={coverFees}
            onCoverFeesChange={setCoverFees}
          />
        )}
        {step === "tributes" && (
          <TributeFormStep
            fromName={fromName} onFromNameChange={setFromName}
            entry={entry} onEntryChange={handleEntryChange}
            tributeNumber={tributeNumber}
            maxTributes={maxTributes}
            submittedTributes={submittedTributes}
            failedTributes={failedTributes}
            onRetry={handleRetry}
          />
        )}
        {step === "success" && (
          <SuccessStep
            tier={tier}
            submittedCount={submittedCount}
            maxTributes={maxTributes}
            skipToTributes={skipToTributes}
          />
        )}

        {step === "donation" && (
          <p style={{ fontSize: "11px", color: "#BBB", textAlign: "center", margin: "12px 0 0" }}>
            Questions? <a href="mailto:info@decatureducationfoundation.org" style={{ color: "#999", textDecoration: "none" }}>info@decatureducationfoundation.org</a>
          </p>
        )}

        {submitError && step === "tributes" && (
          <p style={{ color: "#D32F2F", fontSize: "13px", textAlign: "center", margin: "8px 0 0" }}>
            {submitError}
          </p>
        )}

        {/* Navigation — hidden during direct-form donation step (Bloomerang has its own submit) */}
        {step !== "donation" && <div style={{ marginTop: "20px", display: "flex", justifyContent: step === "success" ? "center" : "space-between", alignItems: "center" }}>
          {step === "success" ? (
            <button
              onClick={onClose}
              style={{
                background: BRAND.primary, color: "#fff", border: "none",
                borderRadius: "6px", padding: "13px 32px", fontSize: "14px",
                fontWeight: 600, cursor: "pointer",
              }}
            >
              View Tribute Wall
            </button>
          ) : (
            <>
              {step === "tributes" && submittedCount > 0 ? (
                <button
                  onClick={handleDone}
                  style={{
                    background: "none", border: "1.5px solid #DDD", borderRadius: "6px",
                    padding: "10px 20px", fontSize: "13px", fontWeight: 600,
                    color: "#555", cursor: "pointer",
                  }}
                >
                  Done
                </button>
              ) : showBack ? (
                <button
                  onClick={goBack}
                  style={{
                    background: "none", border: "none", color: "#888",
                    fontSize: "14px", cursor: "pointer", padding: "8px 0",
                  }}
                >
                  Back
                </button>
              ) : <div />}
              <button
                onClick={canProceed && !processing ? goNext : undefined}
                style={{
                  background: canProceed && !processing ? BRAND.primary : "#DDD",
                  color: "#fff", border: "none", borderRadius: "6px",
                  padding: "13px 28px", fontSize: "14px", fontWeight: 600,
                  cursor: canProceed && !processing ? "pointer" : "default",
                  display: "flex", alignItems: "center", gap: "8px",
                }}
              >
                {processing && (
                  <span style={{
                    display: "inline-block", width: "14px", height: "14px",
                    border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                    borderRadius: "50%", animation: "spin 0.6s linear infinite",
                  }} />
                )}
                {processing ? (step === "tributes" ? "Submitting..." : "Processing...") : nextLabel}
              </button>
            </>
          )}
        </div>}
        {step === "tributes" && !submitError && (
          <p style={{ fontSize: "11px", color: "#BBB", textAlign: "center", margin: "8px 0 0" }}>
            Tributes will be live within one business day.
          </p>
        )}
        {step !== "donation" && (
          <p style={{ fontSize: "11px", color: "#BBB", textAlign: "center", margin: "12px 0 0" }}>
            Questions? <a href="mailto:info@decatureducationfoundation.org" style={{ color: "#999", textDecoration: "none" }}>info@decatureducationfoundation.org</a>
          </p>
        )}
      </div>
      {showCloseConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px",
        }}>
          <div style={{
            background: "#fff", borderRadius: "10px", maxWidth: "380px", width: "100%",
            padding: "24px", boxShadow: "0 16px 48px rgba(0,0,0,0.22)", textAlign: "center",
          }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "18px", color: "#1A1A1A", margin: "0 0 8px", fontWeight: 800,
            }}>
              Close before finishing?
            </h3>
            <p style={{ fontSize: "14px", color: "#555", margin: "0 0 20px", lineHeight: 1.45 }}>
              {step === "tributes"
                ? "Your unsubmitted tribute will be lost. Submitted tributes are saved."
                : "Your progress will be lost."}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button
                onClick={function () { setShowCloseConfirm(false); }}
                style={{
                  background: "none", border: "1.5px solid #DDD", borderRadius: "6px",
                  padding: "10px 18px", fontSize: "13px", fontWeight: 600,
                  color: "#555", cursor: "pointer",
                }}
              >
                Keep Going
              </button>
              <button
                onClick={function () { setShowCloseConfirm(false); onClose(); }}
                style={{
                  background: BRAND.primary, color: "#fff", border: "none",
                  borderRadius: "6px", padding: "10px 18px", fontSize: "13px",
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
